// Window controls in the page's top row: a host whose window hides its title
// bar says so through `MeExtensions.titleBar`. The mac desktop app puts the
// traffic lights over the left end; on Windows and Linux the host hands over
// minimize / maximize / close (`windowControls`) and the shell draws them at
// the right end, level with the header row's own buttons
// (`WindowControlButtons`). The shell keeps the left corner free of its own
// content, header rows that reach the right corner keep their end free
// (`useTitleBarClear`), and the strip is a window drag region (styles:
// `[data-title-bar-drag]`; what can be pressed inside it stays pressable).
// Without a source (a browser) nothing changes.
import {
  type CSSProperties,
  type RefObject,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { MeAppContext } from "../app/context";

/** The ends the controls cover, in CSS px from the window's top corners. */
export interface TitleBarArea {
  /** From the left edge to just past the controls at the left (0: none there). */
  left: number;
  /** From the right edge to just past the controls at the right (0: none there). */
  right: number;
  /** Height of the strip the controls sit in (they are centered in it). */
  height: number;
}

/**
 * Where the controls are now and when that changes. `get` is null while there
 * are none (full screen). Plain functions: they are called unbound.
 */
export interface TitleBarSource {
  get(): TitleBarArea | null;
  subscribe(onChange: () => void): () => void;
  /**
   * Shows or hides the controls, where the host can (the mac desktop app).
   * While hidden the shell keeps no room for them.
   */
  setControlsVisible?(visible: boolean): void;
  /** Minimize / maximize / close for the shell to draw at the right end. */
  windowControls?: WindowControls;
}

/** The window's buttons a host hands over (Windows, Linux). Plain functions. */
export interface WindowControls {
  minimize(): void;
  /** Maximizes, or restores when maximized. */
  toggleMaximize(): void;
  close(): void;
  isMaximized(): boolean;
  onMaximizedChange(handler: (maximized: boolean) => void): () => void;
}

/** The size of each window button and the room between them. */
export const WINDOW_BUTTON_SIZE = 26;
export const WINDOW_BUTTON_GAP = 4;
/**
 * From the window's right edge to the last button's: the frame's 8 and the
 * header row's own 16, so the close button stands where the row's last action
 * would.
 */
export const WINDOW_BUTTONS_END = 24;
/** The window buttons' top: centered on the panel's first header row (8 + 22). */
export const WINDOW_BUTTONS_TOP = 30 - WINDOW_BUTTON_SIZE / 2;
/** From the window's right edge to past the window buttons. */
const WINDOW_BUTTONS_RIGHT =
  WINDOW_BUTTONS_END + 3 * WINDOW_BUTTON_SIZE + 2 * WINDOW_BUTTON_GAP;

const noSubscribe = () => () => {};

/**
 * The covered ends now, or null. Read while rendering, so the first paint
 * already leaves them free (no shift on load).
 */
export function useTitleBarArea(
  source: TitleBarSource | undefined,
): TitleBarArea | null {
  const subscribe = source?.subscribe ?? noSubscribe;
  // Two numbers rather than one object: a snapshot must stay the same value
  // while nothing changed.
  const left = useSyncExternalStore(
    subscribe,
    () => source?.get()?.left ?? 0,
    () => 0,
  );
  const right = useSyncExternalStore(
    subscribe,
    () => source?.get()?.right ?? 0,
    () => 0,
  );
  const height = useSyncExternalStore(
    subscribe,
    () => source?.get()?.height ?? 0,
    () => 0,
  );
  // The buttons the shell draws itself take their room at the right.
  const drawn = source?.windowControls ? WINDOW_BUTTONS_RIGHT : 0;
  return useMemo(() => {
    const end = Math.max(right, drawn);
    return (left > 0 || end > 0) && height > 0
      ? { left, right: end, height }
      : null;
  }, [left, right, drawn, height]);
}

/**
 * The covered ends for layers drawn over the whole window (the media
 * viewers), from the host's source. Null outside the Me app.
 */
export function useWindowCorner(): TitleBarArea | null {
  return useTitleBarArea(useContext(MeAppContext)?.extensions.titleBar);
}

/** Between the controls at the right and what a header row keeps before them. */
const CLEAR_GAP = 8;

/**
 * For a header row: when its top is in the controls' strip and its right end
 * reaches under the controls at the right, the right padding that keeps its
 * end (the actions) left of them; undefined otherwise. Measured from where the
 * row is, so it follows every layout: the row at the right of a split, the
 * list alone, a side view, the sidebar folded or not. `padding` is the row's
 * own right padding, never lessened.
 */
export function useTitleBarClear(
  row: RefObject<HTMLElement | null>,
  padding = 16,
): CSSProperties | undefined {
  const area = useWindowCorner();
  const right = area?.right ?? 0;
  const height = area?.height ?? 0;
  const [clear, setClear] = useState(0);
  // frontend-development: not a fetch; measures the row before it is painted
  // and again when its size changes (the columns beside it moved).
  useLayoutEffect(() => {
    const el = row.current;
    if (!el || right === 0) {
      setClear(0);
      return;
    }
    const read = () => {
      const box = el.getBoundingClientRect();
      const reach = box.right - (window.innerWidth - right);
      setClear(box.top < height && reach > 0 ? Math.ceil(reach) : 0);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    // The border box: the padding set here does not change it.
    const observer = new ResizeObserver(read);
    observer.observe(el, { box: "border-box" });
    return () => observer.disconnect();
  }, [row, right, height]);
  return clear > 0
    ? { paddingRight: Math.max(padding, clear + CLEAR_GAP) }
    : undefined;
}
